import { useEffect, useState } from 'react'
import { ConvexHttpClient } from 'convex/browser'
import { anyApi } from 'convex/server'
import { Sparkles } from 'lucide-react'

/**
 * Zero-click share landing — the viral destination for links shared from the
 * landing site (co-palms.altuzar.dev/c/<token>). Renders the public comparison card via the
 * Convex `comparisons.getByShareToken` query and a "Read my palm" CTA → store.
 *
 * Uses ConvexHttpClient + anyApi so the web app needs no generated Convex types.
 * Falls back gracefully when VITE_CONVEX_URL isn't configured.
 */

type Side = {
    archetype: string | null
    elementalHand: string | null
    hand: string
    imageUrl: string | null
} | null

type Result = {
    score: number
    title: string
    aligned: string[]
    diverged: string[]
    verdict: string
} | null

type Comparison = {
    status: string
    result: Result
    a: Side
    b: Side
} | null

const APP_CTA_URL =
    import.meta.env.VITE_IOS_APP_URL || import.meta.env.VITE_APP_URL || 'https://co-palms.altuzar.dev'

export function ShareCard({ token }: { token: string }) {
    const [state, setState] = useState<'loading' | 'ready' | 'pending' | 'missing' | 'unconfigured'>(
        'loading'
    )
    const [data, setData] = useState<Comparison>(null)

    useEffect(() => {
        const convexUrl = import.meta.env.VITE_CONVEX_URL
        if (!convexUrl) {
            setState('unconfigured')
            return
        }
        const client = new ConvexHttpClient(convexUrl)
        client
            .query(anyApi.comparisons.getByShareToken, { shareToken: token })
            .then((result: Comparison) => {
                if (!result) return setState('missing')
                setData(result)
                setState(result.status === 'ready' && result.result ? 'ready' : 'pending')
            })
            .catch(() => setState('missing'))
    }, [token])

    return (
        <div className="min-h-screen bg-[#0B0A1F] text-[#F4F1FF] font-sans flex flex-col items-center px-4 py-10">
            <div className="flex items-center gap-2 mb-8">
                <Sparkles className="w-5 h-5 text-[#E7C36B]" />
                <span className="text-sm font-semibold tracking-widest uppercase text-[#B6AFD8]">
                    Co-Palms
                </span>
            </div>

            {state === 'loading' && <p className="text-[#B6AFD8]">Reading the connection…</p>}

            {(state === 'missing' || state === 'unconfigured') && (
                <div className="text-center max-w-sm">
                    <p className="text-xl font-semibold mb-2">This comparison isn’t available.</p>
                    <p className="text-[#B6AFD8] mb-8">
                        Read your own palm and find your twin flame — or your opposite.
                    </p>
                </div>
            )}

            {state === 'pending' && (
                <div className="text-center max-w-sm">
                    <p className="text-xl font-semibold mb-2">This reading is still being traced…</p>
                    <p className="text-[#B6AFD8] mb-8">Check back in a moment.</p>
                </div>
            )}

            {state === 'ready' && data?.result && (
                <div className="w-full max-w-md rounded-3xl border border-[#2A2550] bg-gradient-to-br from-[#1C1745] via-[#0B0A1F] to-[#050410] p-6 shadow-2xl">
                    <p className="text-center text-[10px] font-semibold tracking-[0.2em] uppercase text-[#B6AFD8]">
                        Co-Palms · Compatibility
                    </p>

                    <div className="mt-5 flex items-center justify-between gap-3">
                        <Palm side={data.a} />
                        <div className="flex h-20 w-20 shrink-0 flex-col items-center justify-center rounded-full border-2 border-[#E7C36B]">
                            <span className="text-3xl font-bold" style={{ fontFamily: 'Georgia, serif' }}>
                                {data.result.score}
                            </span>
                            <span className="text-[10px] text-[#B6AFD8]">/100</span>
                        </div>
                        <Palm side={data.b} />
                    </div>

                    <h1
                        className="mt-5 text-center text-3xl font-bold"
                        style={{ fontFamily: 'Georgia, serif' }}
                    >
                        {data.result.title}
                    </h1>

                    <ul className="mt-5 space-y-2">
                        {data.result.aligned.map((line, i) => (
                            <li key={`a${i}`} className="flex gap-2 text-sm">
                                <span className="text-[#7DD3A8]">✓</span>
                                <span>{line}</span>
                            </li>
                        ))}
                        {data.result.diverged.map((line, i) => (
                            <li key={`d${i}`} className="flex gap-2 text-sm">
                                <span className="text-[#E7C36B]">⤳</span>
                                <span>{line}</span>
                            </li>
                        ))}
                    </ul>

                    <p className="mt-5 text-center text-lg leading-relaxed">{data.result.verdict}</p>

                    <p className="mt-4 text-center text-xs font-semibold tracking-widest text-[#B6AFD8]">
                        @co_palms
                    </p>
                </div>
            )}

            {state !== 'loading' && (
                <a
                    href={APP_CTA_URL}
                    className="mt-10 inline-flex items-center gap-2 rounded-full bg-[#E7C36B] px-8 py-4 text-base font-semibold text-[#0B0A1F] transition-transform hover:scale-105"
                >
                    Read my palm
                </a>
            )}
        </div>
    )
}

function Palm({ side }: { side: Side }) {
    return (
        <div className="flex flex-1 flex-col items-center gap-2">
            {side?.imageUrl ? (
                <img
                    src={side.imageUrl}
                    alt={side.archetype ?? 'palm'}
                    className="h-24 w-20 rounded-xl object-cover"
                />
            ) : (
                <div className="flex h-24 w-20 items-center justify-center rounded-xl bg-[#16132E] text-2xl">
                    ✋
                </div>
            )}
            <span className="max-w-full truncate text-xs font-semibold text-[#E7C36B]">
                {side?.archetype ?? 'Unknown'}
            </span>
        </div>
    )
}
